import { z } from 'zod';
import {
  BoardKind,
  BoardVisibility,
  CoverMode,
  HexColor,
  Id,
  JsonObject,
  PermissionMask,
  SmartBoardSort,
  Timestamp,
  Uri,
  atLeastOneField,
  cursorPage,
} from './common.js';
import { CustomFieldValue } from './customFields.js';

export const Library = z.object({
  id: Id,
  workspace_id: Id,
  name: z.string().max(120),
  description: z.string().max(2000).nullable().optional(),
  icon: z.string().max(64).nullable().optional(),
  sort_order: z.int(),
  is_default: z.boolean(),
  root_board_count: z.int().optional(),
  asset_count_deep: z.int().optional(),
  created_at: Timestamp,
});
export type Library = z.infer<typeof Library>;

export const LibraryRef = z.object({ id: Id, name: z.string() });
export type LibraryRef = z.infer<typeof LibraryRef>;

export const LibraryCreate = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(2000).optional(),
  icon: z.string().max(64).optional(),
  sort_order: z.int().optional(),
});
export type LibraryCreate = z.infer<typeof LibraryCreate>;

export const LibraryUpdate = atLeastOneField(
  z.object({
    name: z.string().min(1).max(120).optional(),
    description: z.string().max(2000).nullable().optional(),
    icon: z.string().max(64).nullable().optional(),
    sort_order: z.int().optional(),
  }),
);
export type LibraryUpdate = z.infer<typeof LibraryUpdate>;

export const Breadcrumb = z.object({ id: Id, name: z.string() });
export type Breadcrumb = z.infer<typeof Breadcrumb>;

export const BoardRef = z.object({
  id: Id,
  name: z.string(),
  library_id: Id,
  breadcrumbs: z.array(Breadcrumb),
});
export type BoardRef = z.infer<typeof BoardRef>;

/** Signed WebP thumbnail URLs by long-edge width; null = rendition not ready. */
export const ThumbnailSet = z.object({
  '256': Uri.nullable().optional(),
  '512': Uri.nullable().optional(),
  '1024': Uri.nullable().optional(),
  '2048': Uri.nullable().optional(),
  jpeg_1024: Uri.nullable().optional(),
});
export type ThumbnailSet = z.infer<typeof ThumbnailSet>;

export const BoardCover = z.object({
  asset_id: Id.nullable().optional(),
  thumbnails: ThumbnailSet.optional(),
  aspect_ratio: z.number().nullable().optional(),
});
export type BoardCover = z.infer<typeof BoardCover>;

/** Maximum nesting depth (01 §2.5). */
export const BOARD_MAX_DEPTH = 32;

export const Board = z.object({
  id: Id,
  workspace_id: Id,
  library_id: Id,
  parent_board_id: Id.nullable(),
  name: z.string().max(200),
  description: z.string().max(4000).nullable().optional(),
  path: z.string(),
  depth: z.int().min(0).max(BOARD_MAX_DEPTH),
  cover_asset_id: Id.nullable().optional(),
  cover_mode: CoverMode,
  cover: BoardCover.optional(),
  color: HexColor.nullable().optional(),
  sort_order: z.int(),
  asset_count: z.int(),
  asset_count_deep: z.int(),
  child_board_count: z.int(),
  breadcrumbs: z.array(Breadcrumb).optional(),
  custom_field_values: z.array(CustomFieldValue).optional(),
  effective_mask: PermissionMask.optional(),
  created_by: Id.nullable(),
  created_at: Timestamp,
  updated_at: Timestamp,
  deleted_at: Timestamp.nullable().optional(),
  /** Release 1 S7a: archived (hidden, not purged); distinct from `deleted_at`. */
  archived_at: Timestamp.nullable().optional(),
  // --- release2 (contract) --- smart boards (S3) and retention (S7b); all optional
  kind: BoardKind.optional(),
  /** `SearchFilters` (typed loosely here: search.ts imports assets.ts, which imports this module). */
  smart_filters: JsonObject.nullable().optional(),
  smart_sort: SmartBoardSort.nullable().optional(),
  smart_count: z.int().min(0).nullable().optional(),
  smart_counted_at: Timestamp.nullable().optional(),
  has_retention: z.boolean().optional(),
  // --- release3 (contract) --- private boards (S2, lane D) and follow state (REV-2, lane A)
  visibility: BoardVisibility.optional(),
  owner_member_id: Id.nullable().optional(),
  followed: z.boolean().optional(),
});
export type Board = z.infer<typeof Board>;

export const BoardPage = cursorPage(Board);
export type BoardPage = z.infer<typeof BoardPage>;

export const BoardCreate = z
  .object({
    library_id: Id.optional(),
    parent_board_id: Id.nullable().optional(),
    name: z.string().min(1).max(200),
    description: z.string().max(4000).optional(),
    color: HexColor.optional(),
    sort_order: z.int().optional(),
    // --- release3 (contract) --- S2: private boards (owner defaults to the caller; ADMIN may name someone else)
    visibility: BoardVisibility.optional(),
    owner_member_id: Id.optional(),
  })
  .refine((o) => o.parent_board_id != null || o.library_id !== undefined, {
    message: 'library_id is required when parent_board_id is null',
    path: ['library_id'],
  });
export type BoardCreate = z.infer<typeof BoardCreate>;

export const BoardUpdate = atLeastOneField(
  z.object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(4000).nullable().optional(),
    color: HexColor.nullable().optional(),
    sort_order: z.int().optional(),
    // --- release3 (contract) --- S2: visibility (owner or ADMIN) and ownership transfer
    visibility: BoardVisibility.optional(),
    owner_member_id: Id.optional(),
  }),
);
export type BoardUpdate = z.infer<typeof BoardUpdate>;

export const BoardMove = atLeastOneField(
  z.object({
    parent_board_id: Id.nullable().optional(),
    library_id: Id.optional(),
    sort_order: z.int().optional(),
  }),
);
export type BoardMove = z.infer<typeof BoardMove>;

export const BoardCoverSet = z
  .object({
    cover_mode: CoverMode,
    cover_asset_id: Id.nullable().optional(),
  })
  .refine((o) => o.cover_mode !== 'manual' || !!o.cover_asset_id, {
    message: 'cover_asset_id is required when cover_mode=manual',
    path: ['cover_asset_id'],
  });
export type BoardCoverSet = z.infer<typeof BoardCoverSet>;

export const ReorderRequest = z.object({
  ordered_ids: z.array(Id).min(1).max(5000),
});
export type ReorderRequest = z.infer<typeof ReorderRequest>;

export interface BoardTreeNode {
  id: string;
  name: string;
  depth: number;
  color?: string | null;
  asset_count_deep: number;
  has_children: boolean;
  archived_at?: string | null;
  children: BoardTreeNode[];
}
export const BoardTreeNode: z.ZodType<BoardTreeNode> = z.object({
  id: Id,
  name: z.string(),
  depth: z.int(),
  color: z.string().nullable().optional(),
  asset_count_deep: z.int(),
  has_children: z.boolean(),
  archived_at: Timestamp.nullable().optional(),
  children: z.array(z.lazy(() => BoardTreeNode)),
});

export interface BoardBatchCreateNode {
  name: string;
  children?: BoardBatchCreateNode[];
}
export const BoardBatchCreateNode: z.ZodType<BoardBatchCreateNode> = z.object({
  name: z.string().min(1).max(200),
  children: z.array(z.lazy(() => BoardBatchCreateNode)).optional(),
});

export const BoardBatchCreateRequest = z.object({
  parent_board_id: Id.nullable(),
  library_id: Id.optional(),
  tree: z.array(BoardBatchCreateNode).min(1).max(2000),
});
export type BoardBatchCreateRequest = z.infer<typeof BoardBatchCreateRequest>;

export const BoardBatchNodeResult = z.object({
  path: z.string(),
  board_id: Id,
  reused: z.boolean(),
});
export type BoardBatchNodeResult = z.infer<typeof BoardBatchNodeResult>;

export const BoardBatchCreateResponse = z.object({
  nodes: z.array(BoardBatchNodeResult),
});
export type BoardBatchCreateResponse = z.infer<typeof BoardBatchCreateResponse>;

export const BoardAssetsAdd = z.object({
  asset_ids: z.array(Id).min(1).max(5000),
  copy_from_board_id: Id.nullable().optional(),
  position: z.enum(['top', 'bottom']).default('top'),
});
export type BoardAssetsAdd = z.infer<typeof BoardAssetsAdd>;

/** Inline response of `GET /boards/{id}/breadcrumbs` (not a named component). */
export const BoardBreadcrumbsResponse = z.object({
  library: LibraryRef,
  items: z.array(Breadcrumb),
});
export type BoardBreadcrumbsResponse = z.infer<typeof BoardBreadcrumbsResponse>;
